import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Checkbox,
  Circle,
  Box,
  Button,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Switch,
  Table,
  Tag,
  TagCloseButton,
  TagLabel,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  Tooltip,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import PageHeader from "../components/PageHeader";
import { inviteSubject } from "../utils/inviteSubject";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  getSurvivorPool,
  updateSurvivorPool,
  getSurvivorPayouts,
  saveSurvivorPayouts,
  sendSurvivorInvites,
  sendSurvivorRecapTest,
  updateSurvivorMember,
  removeSurvivorMember,
} from "../api/nflSurvivor";
import { generateInviteLink } from "../api/invite";
import { useNflSeason } from "../hooks/useNflSeason";
import PayoutTotalLine from "../components/PayoutTotalLine";
import { payoutTotalError } from "../utils/payoutTotals";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE = "#00007f";

export default function NflSurvivorPoolSetup() {
  const { poolId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const toast = useToast();
  const { seasonId: hookSeasonId } = useNflSeason();
  const SEASON_ID = location.state?.seasonId ?? hookSeasonId;
  const username = user?.username || "A friend";

  const [pool, setPool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPayouts, setSavingPayouts] = useState(false);
  const [sendingRecap, setSendingRecap] = useState(false);
  const [saveError, setSaveError] = useState("");

  // Core
  const [name, setName] = useState("");
  const [poolDepth, setPoolDepth] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);

  // Survivor settings
  const [allOutRule, setAllOutRule] = useState("split");
  const [rollbackUntilWeek, setRollbackUntilWeek] = useState(9);
  const [buybackEnabled, setBuybackEnabled] = useState(false);
  const [buybackDeadline, setBuybackDeadline] = useState("");
  const [maxEntries, setMaxEntries] = useState(1);
  const [weeklyRecap, setWeeklyRecap] = useState(true);
  const [pickReminders, setPickReminders] = useState(true);

  const [payouts, setPayouts] = useState([]);

  // Invite / message
  const [sendMode, setSendMode] = useState("invite");
  const [emails, setEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [message, setMessage] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [linkLoading, setLinkLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const isOwner = pool && user && pool.owner_user_id === user.id;
  const isAdmin = isOwner || pool?.current_user_is_co_admin;

  function buildInviteMessage(poolName, inviterName, link, depth) {
    // Stated up front so an invitee knows the cost before joining.
    // Omitted entirely when the pool has no buy-in.
    const amount = Number(depth);
    const buyIn =
      depth && !Number.isNaN(amount) && amount > 0
        ? ` The buy-in for this pool is currently $${
            Number.isInteger(amount) ? amount : amount.toFixed(2)
          }.`
        : "";
    return (
      `Hi!\n\nYou've been invited to join "${poolName}" on The Deep End — an NFL Survivor pool — by ${inviterName}.${buyIn}\n\n` +
      `Pick one winner each week. Win and you advance; lose or tie and you're out. A team can only be used once all season.\n\n` +
      `Join here:\n${link}\n\nGood luck!\n— ${inviterName}`
    );
  }

  useEffect(() => {
    if (!SEASON_ID || !poolId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SEASON_ID, poolId]);

  async function load() {
    setLoading(true);
    try {
      const [data, payoutData] = await Promise.all([
        getSurvivorPool(SEASON_ID, poolId),
        getSurvivorPayouts(SEASON_ID, poolId).catch(() => []),
      ]);
      setPool(data);
      setName(data.name || "");
      setPoolDepth(data.pool_depth != null ? String(data.pool_depth) : "");
      setIsPrivate(data.is_private ?? false);
      setAllOutRule(data.all_out_rule || "split");
      setRollbackUntilWeek(data.all_out_rollback_until_week ?? 9);
      setBuybackEnabled(data.buyback_enabled ?? false);
      setBuybackDeadline(data.buyback_deadline_week != null ? String(data.buyback_deadline_week) : "");
      setMaxEntries(data.max_entries_per_user ?? 1);
      setWeeklyRecap(data.weekly_recap_enabled ?? true);
      setPickReminders(data.pick_reminders_enabled ?? true);
      setPayouts(payoutData.length ? payoutData : [{ label: "Winner", amount: "" }]);

      try {
        const { link } = await generateInviteLink({
          game: "nfl_sv", poolId: Number(poolId), seasonId: SEASON_ID,
        });
        setInviteLink(link);
        setMessage(buildInviteMessage(data.name, username, link, data.pool_depth));
      } catch {
        // Link generation is best-effort; the Message mode still works without it.
      }
    } catch (err) {
      setSaveError(err?.response?.data?.detail || "Unable to load pool.");
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setSaveError("");
    try {
      const updated = await updateSurvivorPool(SEASON_ID, poolId, {
        name: name.trim(),
        pool_depth: poolDepth ? Number(poolDepth) : null,
        is_private: isPrivate,
        all_out_rule: allOutRule,
        // Only meaningful under rollback; the server clears it when the
        // rule goes back to split.
        all_out_rollback_until_week:
          allOutRule === "rollback" ? Number(rollbackUntilWeek) : null,
        buyback_enabled: buybackEnabled,
        buyback_deadline_week: buybackEnabled && buybackDeadline ? Number(buybackDeadline) : null,
        max_entries_per_user: Number(maxEntries),
        start_week: 1,
        weekly_recap_enabled: weeklyRecap,
        pick_reminders_enabled: pickReminders,
      });
      setPool(updated);
      toast({ title: "Settings saved.", status: "success", duration: 2000, isClosable: true });
    } catch (err) {
      setSaveError(err?.response?.data?.detail || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSavePayouts() {
    const cleaned = payouts
      .filter((p) => p.label && String(p.amount).trim() !== "" && Number(p.amount) > 0)
      .map((p) => ({ label: p.label, amount: Number(p.amount) }));

    // Allow under, block over — see utils/payoutTotals.js. The server
    // enforces the same rule; this is it said early.
    const overPot = payoutTotalError(cleaned.map((p) => p.amount), pool?.total_pot);
    if (overPot) {
      toast({ title: overPot, status: "error", duration: 6000, isClosable: true });
      return;
    }

    setSavingPayouts(true);
    try {
      const saved = await saveSurvivorPayouts(SEASON_ID, poolId, cleaned);
      setPayouts(saved.length ? saved : [{ label: "Winner", amount: "" }]);
      toast({ title: "Payouts saved.", status: "success", duration: 2000, isClosable: true });
    } catch (err) {
      toast({
        title: err?.response?.data?.detail || "Failed to save payouts.",
        status: "error", duration: 4000, isClosable: true,
      });
    } finally {
      setSavingPayouts(false);
    }
  }

  async function handleSendRecapTest() {
    setSendingRecap(true);
    try {
      const res = await sendSurvivorRecapTest(SEASON_ID, poolId);
      toast({
        title: `Test recap for week ${res.week} sent to your email.`,
        status: "success", duration: 3000, isClosable: true,
      });
    } catch (err) {
      toast({
        title: err?.response?.data?.detail || "Failed to send test recap.",
        status: "error", duration: 4000, isClosable: true,
      });
    } finally {
      setSendingRecap(false);
    }
  }

  // ── Invite helpers ──────────────────────────────────────────────────────
  function addEmail() {
    // Split so a pasted list becomes separate tags rather than one address.
    const parts = emailInput.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    if (!parts.length) return;

    const valid = parts.filter((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    const invalid = parts.filter((e) => !valid.includes(e));
    if (invalid.length) {
      toast({
        title: invalid.length === 1
          ? "Enter a valid email address."
          : `${invalid.length} addresses were not valid and were skipped.`,
        status: "warning", duration: 2500, isClosable: true,
      });
    }
    if (!valid.length) return;

    setEmails((prev) => {
      const next = [...prev];
      valid.forEach((e) => { if (!next.includes(e)) next.push(e); });
      return next;
    });
    setEmailInput("");
  }

  function handleEmailKeyDown(e) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addEmail();
    }
  }

  async function handleTogglePaid(member, checked) {
    try {
      await updateSurvivorMember(SEASON_ID, poolId, member.user_id, { paid: checked });
      await load();
    } catch (err) {
      setSaveError(err?.response?.data?.detail || "Unable to update member.");
    }
  }

  async function handleToggleCoAdmin(member, checked) {
    try {
      await updateSurvivorMember(SEASON_ID, poolId, member.user_id, { is_co_admin: checked });
      await load();
    } catch (err) {
      setSaveError(err?.response?.data?.detail || "Unable to update member.");
    }
  }

  async function handleRemoveMember(member) {
    // Removing a Survivor player also deletes their entries, so name both.
    const entries = (member.entries || []).length;
    const who = member.username || member.email || "this member";
    const ok = window.confirm(
      `Remove ${who} from this pool? Their ${entries} ` +
      `${entries === 1 ? "entry" : "entries"} and all picks will be deleted.`
    );
    if (!ok) return;
    try {
      await removeSurvivorMember(SEASON_ID, poolId, member.user_id);
      toast({ title: "Member removed.", status: "info", duration: 3000, isClosable: true });
      await load();
    } catch (err) {
      setSaveError(err?.response?.data?.detail || "Unable to remove that member.");
    }
  }

  function addAllMembers() {
    const existing = new Set(emails);
    const added = (pool?.members || []).map((m) => m.email).filter((e) => e && !existing.has(e));
    setEmails([...emails, ...added]);
  }

  async function switchToInvite() {
    setSendMode("invite");
    if (inviteLink) {
      setMessage(buildInviteMessage(pool?.name || "", username, inviteLink, pool?.pool_depth));
      return;
    }
    setLinkLoading(true);
    try {
      const { link } = await generateInviteLink({
        game: "nfl_sv", poolId: Number(poolId), seasonId: SEASON_ID,
      });
      setInviteLink(link);
      setMessage(buildInviteMessage(pool?.name || "", username, link, pool?.pool_depth));
    } catch (err) {
      toast({
        title: err?.response?.data?.detail || "Failed to generate invite link.",
        status: "error", duration: 4000, isClosable: true,
      });
    } finally {
      setLinkLoading(false);
    }
  }

  function switchToMessage() {
    setSendMode("message");
    setMessage("");
  }

  async function handleCopyLink() {
    if (!inviteLink) return;
    await navigator.clipboard.writeText(inviteLink);
    toast({ title: "Invite link copied.", status: "success", duration: 2000, isClosable: true });
  }

  async function handleSend() {
    if (emails.length === 0) {
      toast({ title: "Add at least one email address.", status: "warning", duration: 2500, isClosable: true });
      return;
    }
    setSending(true);
    try {
      const subject = inviteSubject(sendMode, pool?.name);
      await sendSurvivorInvites(SEASON_ID, poolId, emails, message, subject, sendMode);
      setEmails([]);
      // A sent message is done with; the invite blurb is kept so the next
      // batch of addresses can go out without retyping it.
      if (sendMode === "message") setMessage("");
      toast({
        title: sendMode === "invite" ? "Invites sent!" : "Message sent!",
        status: "success", duration: 4000, isClosable: true,
      });
    } catch (err) {
      toast({
        title: err?.response?.data?.detail || "Failed to send.",
        status: "error", duration: 4000, isClosable: true,
      });
    } finally {
      setSending(false);
    }
  }

  if (loading) {
    return (
      <Box minH="100vh" bg={PAGE_BLUE} color="white">
        <Flex minH="100vh" align="center" justify="center"><Spinner size="xl" /></Flex>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white">
      <PageHeader
        title="SURVIVOR POOL SETUP"
        onBack={() => navigate(`/nfl-survivor/current/${poolId}`, { state: { seasonId: SEASON_ID } })}
        user={user}
        onLogout={() => { logout(); navigate("/login", { replace: true }); }}
      />

      <Container maxW="container.xl" py={8}>
        <VStack align="stretch" spacing={6}>
          {saveError && <Alert status="error" borderRadius="md"><AlertIcon />{saveError}</Alert>}
          {!isOwner && (
            <Alert status="info" borderRadius="md" color={TOP_BAR_BLUE}>
              <AlertIcon />Only the pool owner can change these settings.
            </Alert>
          )}

          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} alignItems="stretch">

          {/* ── Settings ── */}
          <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={6}>
            <VStack align="stretch" spacing={5}>
              <Heading size="md" color={TOP_BAR_BLUE}>Pool Settings</Heading>

              <Flex direction={{ base: "column", md: "row" }} gap={4}>
                <FormControl isDisabled={!isOwner} flex="1.6">
                  <FormLabel fontSize="sm">Pool Name</FormLabel>
                  <Input value={name} onChange={(e) => setName(e.target.value)} />
                </FormControl>
                <FormControl isDisabled={!isOwner} flex="1">
                  <FormLabel fontSize="sm">Buy-in per entry ($)</FormLabel>
                  <Input type="number" min={1} value={poolDepth} placeholder="Optional"
                         onChange={(e) => setPoolDepth(e.target.value)} />
                </FormControl>
                <FormControl isDisabled={!isOwner} flex="1">
                  <FormLabel fontSize="sm">Max entries per user</FormLabel>
                  <Input type="number" min={1} max={20} value={maxEntries}
                         onChange={(e) => setMaxEntries(e.target.value)} />
                </FormControl>
              </Flex>

              <Divider />

              <HStack justify="space-between">
                <Box>
                  <FormLabel fontSize="sm" mb={0}>Private Pool</FormLabel>
                  <Text fontSize="xs" color="gray.500">Invite only — not listed as joinable</Text>
                </Box>
                <Switch isChecked={isPrivate} colorScheme="blue" isDisabled={!isOwner}
                        onChange={(e) => setIsPrivate(e.target.checked)} />
              </HStack>

              <HStack justify="space-between">
                <Box>
                  <FormLabel fontSize="sm" mb={0}>Weekly Recap Emails</FormLabel>
                  <Text fontSize="xs" color="gray.500">Send a results summary after each week resolves</Text>
                </Box>
                <Switch isChecked={weeklyRecap} colorScheme="blue" isDisabled={!isOwner}
                        onChange={(e) => setWeeklyRecap(e.target.checked)} />
              </HStack>

              <HStack justify="space-between">
                <Box>
                  <FormLabel fontSize="sm" mb={0}>Pick Reminder Emails</FormLabel>
                  <Text fontSize="xs" color="gray.500">Remind players who haven&apos;t picked before kickoff</Text>
                </Box>
                <Switch isChecked={pickReminders} colorScheme="blue" isDisabled={!isOwner}
                        onChange={(e) => setPickReminders(e.target.checked)} />
              </HStack>

              <Divider />
              <Heading size="sm" color={TOP_BAR_BLUE}>Survivor Rules</Heading>

              <FormControl isDisabled={!isOwner}>
                <FormLabel fontSize="sm" mb={1}>If everyone is eliminated in the same week</FormLabel>
                <Select value={allOutRule} onChange={(e) => setAllOutRule(e.target.value)} maxW="380px">
                  <option value="split">Split the pot between them — pool ends</option>
                  <option value="rollback">Roll back — void the week, everyone survives</option>
                </Select>

                {allOutRule === "rollback" && (
                  <Box mt={3}>
                    <FormLabel fontSize="sm" mb={1}>Roll back only before week</FormLabel>
                    <Select
                      value={rollbackUntilWeek}
                      onChange={(e) => setRollbackUntilWeek(Number(e.target.value))}
                      maxW="380px"
                    >
                      {Array.from({ length: 17 }, (_, i) => i + 2).map((w) => (
                        <option key={w} value={w}>Week {w}</option>
                      ))}
                    </Select>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      A wipeout before week {rollbackUntilWeek} voids that week and
                      everyone survives. From week {rollbackUntilWeek} onward the pot
                      is split between whoever was still alive going in.
                    </Text>
                  </Box>
                )}
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {allOutRule === "rollback"
                    ? "That week's picks are thrown out and the teams used become available again."
                    : "The players knocked out that week are declared co-winners."}
                </Text>
              </FormControl>

              <HStack justify="space-between" align="flex-start">
                <Box>
                  <FormLabel fontSize="sm" mb={0}>Allow Buy-Back</FormLabel>
                  <Text fontSize="xs" color="gray.500">
                    An eliminated entry may re-enter once, up to the deadline week.
                  </Text>
                </Box>
                <Switch isChecked={buybackEnabled} colorScheme="blue" isDisabled={!isOwner}
                        onChange={(e) => setBuybackEnabled(e.target.checked)} />
              </HStack>

              {buybackEnabled && (
                <FormControl isDisabled={!isOwner} pl={2}>
                  <FormLabel fontSize="sm" mb={1}>Buy-back deadline week</FormLabel>
                  <Select value={buybackDeadline} placeholder="No deadline" maxW="240px"
                          onChange={(e) => setBuybackDeadline(e.target.value)}>
                    {Array.from({ length: 18 }, (_, i) => i + 1).map((w) => (
                      <option key={w} value={w}>Week {w}</option>
                    ))}
                  </Select>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    After this week, eliminated entries can no longer buy back in.
                  </Text>
                </FormControl>
              )}


              {isOwner && (
                <Flex w="100%" gap={2}>
                  <Button flex="1" size={{ base: "sm", md: "md" }} bg={TOP_BAR_BLUE} color="white"
                          _hover={{ bg: "#00004a" }} isLoading={saving} onClick={handleSave}>
                    Save<Box as="span" display={{ base: "none", sm: "inline" }}> Changes</Box>
                  </Button>
                  <Button flex="1" size={{ base: "sm", md: "md" }} variant="outline"
                          borderColor={TOP_BAR_BLUE} color={TOP_BAR_BLUE} _hover={{ bg: "gray.50" }}
                          onClick={() => navigate(`/nfl-survivor/current/${poolId}`, { state: { seasonId: SEASON_ID } })}>
                    Open Pool
                  </Button>
                  <Button flex="1" size={{ base: "sm", md: "md" }} variant="outline"
                          borderColor="orange.400" color="orange.500" _hover={{ bg: "orange.50" }}
                          isLoading={sendingRecap} onClick={handleSendRecapTest}>
                    <Box as="span" display={{ base: "none", sm: "inline" }}>Send&nbsp;</Box>Test Recap
                  </Button>
                </Flex>
              )}
            </VStack>
          </Box>

          {/* ── Payouts ── */}
          <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={6}>
            <VStack align="stretch" spacing={4}>
              <Flex justify="space-between" align="center">
                <Heading size="md" color={TOP_BAR_BLUE}>Payouts</Heading>
                {pool?.total_pot != null && (
                  <Badge colorScheme="green" fontSize="sm">Pot: ${pool.total_pot}</Badge>
                )}
              </Flex>
              {payouts.map((p, i) => (
                <HStack key={i}>
                  <Input placeholder="Label (e.g. Winner)" value={p.label} isDisabled={!isOwner}
                         onChange={(e) => {
                           const next = [...payouts]; next[i] = { ...next[i], label: e.target.value }; setPayouts(next);
                         }} />
                  <Input type="number" min={1} placeholder="Amount" maxW="140px" value={p.amount} isDisabled={!isOwner}
                         onChange={(e) => {
                           const next = [...payouts]; next[i] = { ...next[i], amount: e.target.value }; setPayouts(next);
                         }} />
                  <IconButton aria-label="Remove payout" icon={<DeleteIcon />} size="sm" isDisabled={!isOwner}
                              onClick={() => setPayouts(payouts.filter((_, j) => j !== i))} />
                </HStack>
              ))}
              {payouts.length > 0 && (
                <PayoutTotalLine
                  amounts={payouts.map((p) => p.amount)}
                  pot={pool?.total_pot}
                />
              )}
              {isOwner && (
                <HStack>
                  <Button size="sm" variant="outline" borderColor={TOP_BAR_BLUE} color={TOP_BAR_BLUE}
                          onClick={() => setPayouts([...payouts, { label: "", amount: "" }])}>
                    + Add Row
                  </Button>
                  <Button size="sm" bg={TOP_BAR_BLUE} color="white" _hover={{ bg: "#00004a" }}
                          isLoading={savingPayouts} onClick={handleSavePayouts}>
                    Save Payouts
                  </Button>
                </HStack>
              )}
            </VStack>
          </Box>

          </SimpleGrid>

          {/* ── Members ── */}
          <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={6}>
            <VStack align="stretch" spacing={4}>
              <HStack justify="space-between" flexWrap="wrap" gap={2}>
                <Heading size="md" color={TOP_BAR_BLUE}>Members</Heading>
                {(() => {
                  const members = pool?.members || [];
                  const paid = members.filter((m) => m.paid).length;
                  return (
                    <HStack spacing={3} flexWrap="wrap">
                      <Text fontSize="sm" color="gray.500">{members.length} joined</Text>
                      <Badge fontSize="sm" px={2}
                             colorScheme={paid === members.length && members.length ? "green" : "orange"}>
                        {paid} / {members.length} paid
                      </Badge>
                    </HStack>
                  );
                })()}
              </HStack>

              {(pool?.members || []).length > 0 ? (
                <Box overflowX="auto" overflowY="auto" maxH="400px">
                  <Table size="sm" variant="simple">
                    <Thead position="sticky" top={0} bg="white" zIndex={1}>
                      <Tr>
                        <Th>Member</Th>
                        <Th>Email</Th>
                        <Th>Entries</Th>
                        <Th textAlign="center">Paid</Th>
                        <Th textAlign="center">Co-Admin</Th>
                        <Th />
                      </Tr>
                    </Thead>
                    <Tbody>
                      {pool.members.map((m) => (
                        <Tr key={m.user_id}>
                          <Td>
                            <HStack spacing={2}>
                              <Circle size="28px" bg={TOP_BAR_BLUE} color="white"
                                      fontWeight="bold" fontSize="xs" flexShrink={0}>
                                {(m.username || "?").charAt(0).toUpperCase()}
                              </Circle>
                              <Text fontWeight="semibold" fontSize="sm">{m.username || "—"}</Text>
                              {m.user_id === pool.owner_user_id && (
                                <Badge colorScheme="blue" fontSize="2xs">Admin</Badge>
                              )}
                            </HStack>
                          </Td>
                          <Td>
                            <Text fontSize="sm" color="gray.600" noOfLines={1}>
                              {m.email || "—"}
                            </Text>
                          </Td>
                          <Td>
                            <HStack spacing={1} flexWrap="wrap">
                              {(m.entries || []).map((e) => (
                                <Badge key={e.id} fontSize="2xs"
                                       colorScheme={e.is_eliminated ? "red" : "green"}>
                                  {e.label || `#${e.entry_no}`}
                                  {e.is_eliminated ? ` out wk${e.eliminated_week}` : " alive"}
                                </Badge>
                              ))}
                            </HStack>
                          </Td>
                          <Td textAlign="center">
                            <Checkbox isChecked={!!m.paid} colorScheme="green"
                                      isDisabled={!isAdmin}
                                      onChange={(e) => handleTogglePaid(m, e.target.checked)} />
                          </Td>
                          <Td textAlign="center">
                            {m.user_id !== pool.owner_user_id && (
                              <Checkbox isChecked={!!m.is_co_admin} colorScheme="blue"
                                        isDisabled={!isOwner}
                                        onChange={(e) => handleToggleCoAdmin(m, e.target.checked)} />
                            )}
                          </Td>
                          <Td textAlign="right" px={1}>
                            {isAdmin && m.user_id !== pool.owner_user_id && (
                              <Tooltip label="Remove member" hasArrow>
                                <IconButton icon={<DeleteIcon />} size="xs" variant="ghost"
                                            colorScheme="red" aria-label="Remove member"
                                            onClick={() => handleRemoveMember(m)} />
                              </Tooltip>
                            )}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              ) : (
                <Text fontSize="sm" color="gray.500">No members yet.</Text>
              )}
            </VStack>
          </Box>

          {/* ── Invite / Message Players ── */}
          {isAdmin && (
            <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={6}>
              <VStack align="stretch" spacing={4}>
                <HStack justify="space-between" align="center">
                  <Heading size="md" color={TOP_BAR_BLUE}>Invite/Message Players</Heading>
                  <Button size="xs" minH="40px" variant="outline" borderColor={TOP_BAR_BLUE}
                          color={TOP_BAR_BLUE} _hover={{ bg: "gray.50" }} onClick={addAllMembers}>
                    Add all members
                  </Button>
                </HStack>

                <HStack spacing={0} borderRadius="md" overflow="hidden"
                        border={`1.5px solid ${TOP_BAR_BLUE}`} alignSelf="flex-start">
                  <Button size="sm" minH="40px" borderRadius="0" px={5}
                          bg={sendMode === "invite" ? TOP_BAR_BLUE : "white"}
                          color={sendMode === "invite" ? "white" : TOP_BAR_BLUE}
                          _hover={{ bg: sendMode === "invite" ? TOP_BAR_BLUE : "gray.50" }}
                          isLoading={sendMode === "invite" && linkLoading}
                          onClick={switchToInvite}>
                    Invite
                  </Button>
                  <Button size="sm" minH="40px" borderRadius="0" px={5}
                          bg={sendMode === "message" ? TOP_BAR_BLUE : "white"}
                          color={sendMode === "message" ? "white" : TOP_BAR_BLUE}
                          _hover={{ bg: sendMode === "message" ? TOP_BAR_BLUE : "gray.50" }}
                          onClick={switchToMessage}>
                    Message
                  </Button>
                </HStack>

                {sendMode === "invite" && inviteLink && (
                  <HStack bg="gray.50" px={3} py={2} borderRadius="md" border="1px solid #e2e8f0">
                    <Text fontSize="xs" color="gray.600" flex="1" isTruncated>{inviteLink}</Text>
                    <Button size="xs" minH="40px" variant="outline" borderColor={TOP_BAR_BLUE}
                            color={TOP_BAR_BLUE} onClick={handleCopyLink} flexShrink={0}>
                      Copy Link
                    </Button>
                  </HStack>
                )}

                <FormControl>
                  <FormLabel fontSize="sm">Email Addresses</FormLabel>
                  <HStack>
                    <Input size="sm" value={emailInput} placeholder="Enter email and press Enter"
                           onChange={(e) => setEmailInput(e.target.value)}
                           onKeyDown={handleEmailKeyDown} />
                    <Button size="sm" minH="40px" bg={TOP_BAR_BLUE} color="white"
                            _hover={{ bg: "#00004a" }} flexShrink={0} onClick={addEmail}>
                      Add
                    </Button>
                  </HStack>
                  {emails.length > 0 && (
                    <Flex mt={2} flexWrap="wrap" gap={2}>
                      {emails.map((addr) => (
                        <Tag key={addr} size="sm" borderRadius="full" variant="solid" colorScheme="blue">
                          <TagLabel>{addr}</TagLabel>
                          <TagCloseButton onClick={() => setEmails(emails.filter((e) => e !== addr))} />
                        </Tag>
                      ))}
                    </Flex>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Message</FormLabel>
                  <Textarea value={message} rows={7} fontSize="sm"
                            placeholder={sendMode === "message" ? "Add message here..." : ""}
                            onChange={(e) => setMessage(e.target.value)} />
                </FormControl>

                <Button bg={TOP_BAR_BLUE} color="white" _hover={{ bg: "#00004a" }}
                        isLoading={sending} onClick={handleSend} alignSelf="flex-start">
                  {sendMode === "invite"
                    ? `Send Invite${emails.length > 1 ? "s" : ""}`
                    : `Send Message${emails.length > 1 ? "s" : ""}`}
                </Button>
              </VStack>
            </Box>
          )}
        </VStack>
      </Container>
    </Box>
  );
}
